import { functions } from "@/main";
import { recallAdminRead, rememberAdminRead } from "@/utils/AdminReadCache";
import {
  MiraInsurer,
  MiraInsurerList,
  MiraInsurerSave,
  MiraInsurerMerge,
  MiraInsurerSpellingMove,
  MiraInsurerSuggestionDismissal,
  UnresolvedInsurerDecision,
  UnresolvedInsurerList,
} from "../models/MiraInsurerAdministration";

/**
 * Mira's insurer list in the IRES Admin Client, through IRES Cloud (`MiraInsurersForAdministration.ts`,
 * documented in Cloud `functions/docs/analytics/mira-loader.md`). Each callable has a `…Development` twin;
 * swap to it **locally only** to drive the twin from a dev server, never commit the swap.
 */
const LIST_CALLABLE = "listMiraInsurersForAdministration";
const SAVE_CALLABLE = "saveMiraInsurerForAdministration";
const LIST_UNRESOLVED_CALLABLE = "listUnresolvedInsurersForAdministration";
const DECIDE_CALLABLE = "decideUnresolvedInsurersForAdministration";
const DISMISS_SUGGESTION_CALLABLE = "dismissInsurerSuggestionForAdministration";
const MOVE_CALLABLE = "moveMiraInsurerSpellingsForAdministration";
const MERGE_CALLABLE = "mergeMiraInsurerForAdministration";

/** The insurers and the spellings decided "not an insurer". `MANAGE_CLIENTS`. */
export async function listMiraInsurers(): Promise<MiraInsurerList> {
  const data = (await functions.httpsCallable(LIST_CALLABLE)()).data as Partial<MiraInsurerList> | null;
  const insurers = data?.insurers;
  const notInsurers = data?.notInsurers;
  const list = {
    insurers: Array.isArray(insurers) ? insurers : [],
    notInsurers: Array.isArray(notInsurers) ? notInsurers : [],
  };
  rememberAdminRead(LIST_CALLABLE, null, list);
  return list;
}

/** The last answer of {@link listMiraInsurers} in this tab, or null (`AdminReadCache.ts`). */
export function cachedMiraInsurers(): MiraInsurerList | null {
  return recallAdminRead(LIST_CALLABLE, null);
}

/** Creates or edits one insurer. Rejects with `details.reason` (KEY_TAKEN, CHANGED_MEANWHILE, CONFLICT, ...). */
export async function saveMiraInsurer(save: MiraInsurerSave): Promise<MiraInsurer> {
  const data = (await functions.httpsCallable(SAVE_CALLABLE)(save)).data as { insurer?: MiraInsurer } | null;
  if (!data?.insurer) {
    throw new Error("The save response carried no insurer.");
  }
  return data.insurer;
}

/** The insurer names the last load could not resolve, grouped, largest first. `MANAGE_CLIENTS`. */
export async function listUnresolvedInsurers(): Promise<UnresolvedInsurerList> {
  const data = (await functions.httpsCallable(LIST_UNRESOLVED_CALLABLE)()).data as Partial<UnresolvedInsurerList> | null;
  const groups = data?.groups;
  const list = {
    groups: Array.isArray(groups) ? groups : [],
    loadedAt: data?.loadedAt ?? null,
    droppedTexts: data?.droppedTexts ?? 0,
    // Absent from an IRES Cloud without the suggestions: the screen then shows none.
    suggestionsAt: typeof data?.suggestionsAt === "number" ? data.suggestionsAt : null,
  };
  rememberAdminRead(LIST_UNRESOLVED_CALLABLE, null, list);
  return list;
}

/** The last answer of {@link listUnresolvedInsurers} in this tab, or null (`AdminReadCache.ts`). */
export function cachedUnresolvedInsurers(): UnresolvedInsurerList | null {
  return recallAdminRead(LIST_UNRESOLVED_CALLABLE, null);
}

/** Decides unresolved spellings. Rejects with `details.reason` (CONFLICT, NO_INSURER, ...). */
export async function decideUnresolvedInsurers(decision: UnresolvedInsurerDecision): Promise<void> {
  await functions.httpsCallable(DECIDE_CALLABLE)(decision);
}

/** Hides a group's suggestion (#2610125). Rejects with `details.reason` (NO_SUGGESTION, CHANGED_MEANWHILE, ...). */
export async function dismissInsurerSuggestion(dismissal: MiraInsurerSuggestionDismissal): Promise<void> {
  await functions.httpsCallable(DISMISS_SUGGESTION_CALLABLE)(dismissal);
}

/** Moves spellings and words to another insurer, a new one, Kein Versicherer or back to open (#2610125). */
export async function moveMiraInsurerSpellings(move: MiraInsurerSpellingMove): Promise<void> {
  await functions.httpsCallable(MOVE_CALLABLE)(move);
}

/** Merges one insurer into another (#2610125). Rejects with `details.reason` (CHANGED_MEANWHILE, CONFLICT, ...). */
export async function mergeMiraInsurer(merge: MiraInsurerMerge): Promise<void> {
  await functions.httpsCallable(MERGE_CALLABLE)(merge);
}
